import { createContext, useContext, useMemo, useEffect, useRef, type ReactNode } from 'react';

import { useApi } from './api-context';

import {
    ServiceManager,
    DataLoaderService,
    ViewportService,
    ViewportLayoutService,
    WorkflowRegistry,
    DisplayFeatureManager,
    DisplayObjectRegistry,
    InteractionService,
    registerDisplayObjects,
    type IWorkflowService,
    CurrentLocationService,
    CprAngleService,
    CarmViewService,
    Color3DService,
    ViewportDataService,
    WindowLevelService,
    ViewportSnapshotService,
} from '@/visualization';
import { SizingStateService } from '@/visualization/services/sizing/sizing-state-service';
import { ToolGroupManager } from '@/visualization/tools/tool-group-manager';
import { registerDefaultWorkflows } from '@/workflows';
import { DosimetryJobService } from '@/workflows/oncology/dosimetry/services/dosimetry-job-service';
import { DosimetryStateService } from '@/workflows/oncology/dosimetry/services/dosimetry-state-service';
import { StrategyStateService } from '@/workflows/vascular/evar/services/strategy-state-service';

interface VisualizationContextValue {
    serviceManager: ServiceManager;
    /** Returns the current ServiceManager instance. */
    getServiceManager: () => ServiceManager;
    getDataLoader: () => DataLoaderService;
    getViewportService: () => ViewportService;
    getViewportLayoutService: () => ViewportLayoutService;
    getInteractionService: () => InteractionService;
    /**
     * Get a workflow service by workflow ID.
     * Uses the WorkflowRegistry to create the appropriate service type.
     */
    getWorkflowService: (workflowId: string) => IWorkflowService;
    /** Set the currently active workflow service (called by useViewerSession). */
    setActiveWorkflowService: (service: IWorkflowService) => void;
    /** Get the currently active workflow service. */
    getActiveWorkflowService: () => IWorkflowService | null;
}

const VisualizationContext = createContext<VisualizationContextValue | undefined>(undefined);

/**
 * Creates and configures a new ServiceManager with all visualization services.
 */
function createServiceManager(dataService: unknown, api: { jobs: unknown; data: unknown }): ServiceManager {
    const sm = new ServiceManager();

    sm.registerServices([
        [DataLoaderService.REGISTRATION, { dataService }],
        DisplayFeatureManager.REGISTRATION,
        DisplayObjectRegistry.REGISTRATION,
        ViewportDataService.REGISTRATION,
        CurrentLocationService.REGISTRATION,
        CprAngleService.REGISTRATION,
        CarmViewService.REGISTRATION,
        WindowLevelService.REGISTRATION,
        Color3DService.REGISTRATION,
        ToolGroupManager.REGISTRATION,
        InteractionService.REGISTRATION,
        ViewportService.REGISTRATION,
        ViewportLayoutService.REGISTRATION,
        ViewportSnapshotService.REGISTRATION,
        SizingStateService.REGISTRATION,
        StrategyStateService.REGISTRATION,
        DosimetryStateService.REGISTRATION,
        [DosimetryJobService.REGISTRATION, { api: { jobs: api.jobs, data: api.data } }],
    ]);

    // Register DisplayObject factories
    // This must happen after DisplayObjectRegistry is registered but before viewports are created
    const registry = sm.getService<DisplayObjectRegistry>(DisplayObjectRegistry.SERVICE_NAME);
    registerDisplayObjects(registry);

    // Register workflow factories (must happen before any WorkflowRegistry.getFactory calls)
    registerDefaultWorkflows();

    return sm;
}

/**
 * Create a workflow service instance from the registry.
 * The service is created fresh each time (no caching at context level).
 */
function createWorkflowService(serviceManager: ServiceManager, workflowId: string): IWorkflowService {
    // Get the factory from the registry
    const factory = WorkflowRegistry.getFactory(workflowId);

    // Create the service with the service context
    const serviceContext = {
        services: serviceManager,
        config: {},
    };

    return factory(serviceContext);
}

export function VisualizationProvider({ children }: { children: ReactNode }) {
    const api = useApi();

    // Use ref to hold ServiceManager — survives HMR reloads
    const serviceManagerRef = useRef<ServiceManager | null>(null);

    // Cache for workflow services (created on demand)
    const workflowServicesRef = useRef<Map<string, IWorkflowService>>(new Map());

    // Currently active workflow service (set by useViewerSession)
    const activeWorkflowServiceRef = useRef<IWorkflowService | null>(null);

    // Get or create ServiceManager
    const getServiceManager = (): ServiceManager => {
        if (!serviceManagerRef.current) {
            serviceManagerRef.current = createServiceManager(api.data, { jobs: api.jobs, data: api.data });
        }
        return serviceManagerRef.current;
    };

    // Ensure ServiceManager exists during render
    const serviceManager = getServiceManager();

    const value = useMemo(
        () => ({
            serviceManager,
            getServiceManager,
            getDataLoader: () =>
                getServiceManager().getService<DataLoaderService>(DataLoaderService.SERVICE_NAME),
            getViewportService: () =>
                getServiceManager().getService<ViewportService>(ViewportService.SERVICE_NAME),
            getViewportLayoutService: () =>
                getServiceManager().getService<ViewportLayoutService>(ViewportLayoutService.SERVICE_NAME),
            getInteractionService: () =>
                getServiceManager().getService<InteractionService>(InteractionService.SERVICE_NAME),
            getWorkflowService: (workflowId: string): IWorkflowService => {
                // Check cache first
                const cached = workflowServicesRef.current.get(workflowId);
                if (cached) {
                    return cached;
                }

                // Create new workflow service
                const workflowService = createWorkflowService(getServiceManager(), workflowId);
                workflowServicesRef.current.set(workflowId, workflowService);
                return workflowService;
            },
            setActiveWorkflowService: (service: IWorkflowService) => {
                activeWorkflowServiceRef.current = service;
            },
            getActiveWorkflowService: () => activeWorkflowServiceRef.current,
        }),
        [serviceManager]
    );

    // Cleanup: dispose ServiceManager on unmount
    useEffect(() => {
        return () => {
            serviceManagerRef.current?.dispose();
            serviceManagerRef.current = null;
            workflowServicesRef.current.clear();
        };
    }, []);

    return <VisualizationContext.Provider value={value}>{children}</VisualizationContext.Provider>;
}

export function useVisualization(): VisualizationContextValue {
    const context = useContext(VisualizationContext);
    if (!context) {
        throw new Error('useVisualization must be used within a VisualizationProvider');
    }
    return context;
}
